import { useRef } from "react";
import { StickerConfig, Unit, Shape, Material } from "@/lib/sticker";
import { StickerMode } from "@/hooks/sticker/useStickerMode";
import { Separator } from "@/components/ui/separator";
import { ResponsiveLayout } from "@/components/common/ResponsiveLayout";
import { StickerPreviewArea } from "./PreviewArea";
import { ShapeSizeSection } from "./ShapeSizeSection";
import { MaterialSection } from "./MaterialSection";
import { ModelSection } from "./ModelSection";
import { CanvasPreviewSection } from "./CanvasPreviewSection";

interface StickerCustomizerProps {
  stickerConfig: StickerConfig;
  setStickerConfig: (config: StickerConfig) => void;
  selectedModel: string;
  setSelectedModel: (model: string) => void;
  stickerMode: StickerMode;
}

export const StickerCustomizer = ({
  stickerConfig,
  setStickerConfig,
  selectedModel,
  setSelectedModel,
  stickerMode,
}: StickerCustomizerProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageUpload = (file: File) => {
    setStickerConfig({
      ...stickerConfig,
      image: URL.createObjectURL(file),
    });
  };

  const handleRemoveImage = () => {
    setStickerConfig({ ...stickerConfig, image: null });
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleShapeChange = (shape: string) => {
    setStickerConfig({ ...stickerConfig, shape: shape as Shape });
  };

  const handleSizeChange = (field: string, value: string) => {
    const parsedValue = parseFloat(value) || 0;
    const clampedValue = Math.max(0, parsedValue);
    setStickerConfig({
      ...stickerConfig,
      size: { ...stickerConfig.size, [field]: clampedValue },
    });
  };

  const handleMaterialChange = (material: Material) => {
    setStickerConfig({ ...stickerConfig, material });
  };

  const handleUnitChange = (newUnit: Unit) => {
    if (newUnit) {
      setStickerConfig({ ...stickerConfig, unit: newUnit });
    }
  };

  return (
    <ResponsiveLayout>
      {/* Section 1: Sticker Preview */}
      <div className="h-full overflow-y-auto">
        <div className="p-4">
          <StickerPreviewArea
            stickerConfig={stickerConfig}
            onImageUpload={handleImageUpload}
            onRemoveImage={handleRemoveImage}
          />
        </div>
        <div className="hidden md:block">
          <Separator />
        </div>
      </div>

      {/* Section 2: Shape & Size */}
      <div className="h-full overflow-y-auto">
        <ShapeSizeSection
          stickerConfig={stickerConfig}
          onShapeChange={handleShapeChange}
          onSizeChange={handleSizeChange}
          onUnitChange={handleUnitChange}
        />
        <div className="hidden md:block">
          <Separator />
        </div>
      </div>

      {/* Section 3: Material - Only show in decal mode */}
      {stickerMode === "decal" && (
        <div className="h-full overflow-y-auto">
          <MaterialSection
            selectedMaterial={stickerConfig.material}
            onMaterialChange={handleMaterialChange}
          />
          <div className="hidden md:block">
            <Separator />
          </div>
        </div>
      )}

      {/* Section 3b: Canvas Preview - Only show in texture mode */}
      {stickerMode === "texture" && (
        <div className="h-full overflow-y-auto">
          <CanvasPreviewSection />
          <div className="hidden md:block">
            <Separator />
          </div>
        </div>
      )}

      {/* Section 4: Model Selection */}
      <div className="h-full overflow-y-auto">
        <ModelSection
          selectedModel={selectedModel}
          onModelChange={setSelectedModel}
        />
      </div>
    </ResponsiveLayout>
  );
};
